/**
 * useVerifiedTracking — "Traffic Guardian" analytics gatekeeper
 *
 * Prevents external tracking scripts (GA, TikTok Pixel, etc.) from loading
 * until the visitor proves they are human through one of three signals:
 *   1. Scrolling the page
 *   2. Moving the mouse / touching the screen
 *   3. Remaining on the page for > 3 seconds (dwell-time heuristic)
 *
 * Once ANY signal fires the scripts are injected exactly once.
 * On unmount every listener is cleaned up automatically.
 */

import { useEffect, useRef } from "react";

/* ------------------------------------------------------------------ */
/*  Configuration – add / remove script payloads here                 */
/* ------------------------------------------------------------------ */

/** Google Analytics measurement ID */
const GA_ID = "G-RZ9C00F2P6";

/** TikTok Pixel ID */
const TIKTOK_ID = "D6C69DRC77U43E96IIS0";

/** Microsoft Clarity Project ID */
const CLARITY_ID = "w80q8k0wxg";

/** Minimum dwell-time (ms) before we consider the visitor human */
const DWELL_MS = 5_000;

/* ------------------------------------------------------------------ */
/*  Script injection helpers                                           */
/* ------------------------------------------------------------------ */

/** Inject the Google Analytics (gtag.js) snippet */
function injectGA() {
  // Guard: don't double-inject
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_ID}"]`)) return;

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialise the dataLayer & config
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer.push(args);
  }
  gtag("js", new Date());
  gtag("config", GA_ID);
}

/** Inject the TikTok Pixel snippet */
function injectTikTok() {
  // Guard: don't double-inject – check for internal _t property on the global
  const ttqGlobal = window.ttq as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (ttqGlobal && ttqGlobal._t && Object.keys(ttqGlobal._t).length > 0) return;

  /* Minified official TikTok loader – identical to index.html version */
  const w = window as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  const t = "ttq";
  w.TiktokAnalyticsObject = t;
  const ttq: any = (w[t] = w[t] || []); // eslint-disable-line @typescript-eslint/no-explicit-any
  ttq.methods = [
    "page", "track", "identify", "instances", "debug",
    "on", "off", "once", "ready", "alias", "group",
    "enableCookie", "disableCookie", "holdConsent",
    "revokeConsent", "grantConsent",
  ];
  ttq.setAndDefer = function (obj: any, method: string) { // eslint-disable-line @typescript-eslint/no-explicit-any
    obj[method] = function () {
      obj.push([method].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
  ttq.instance = function (id: string) {
    const e = ttq._i[id] || [];
    for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
    return e;
  };
  ttq.load = function (pixelId: string, opts?: Record<string, unknown>) {
    const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[pixelId] = [];
    ttq._i[pixelId]._u = r;
    ttq._t = ttq._t || {};
    ttq._t[pixelId] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[pixelId] = opts || {};
    const n = document.createElement("script");
    n.type = "text/javascript";
    n.async = true;
    n.src = r + "?sdkid=" + pixelId + "&lib=" + t;
    const first = document.getElementsByTagName("script")[0];
    first.parentNode?.insertBefore(n, first);
  };

  ttq.load(TIKTOK_ID);
  ttq.page({}, { content_id: "drshimaaanwer_home" });
}

/** Inject Microsoft Clarity snippet */
function injectClarity() {
  if ((window as any).clarity) return;

  (function (c: any, l: any, a: any, r: any, i: any) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode?.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
}

/* ------------------------------------------------------------------ */
/*  Global type augmentation                                           */
/* ------------------------------------------------------------------ */
declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/* ------------------------------------------------------------------ */
/*  The hook                                                           */
/* ------------------------------------------------------------------ */

export function useVerifiedTracking() {
  /** Ref ensures the injection runs at most once across re-renders */
  const injectedRef = useRef(false);

  useEffect(() => {
    /** Core handler – injects all scripts then removes every listener */
    function activate() {
      if (injectedRef.current) return;
      injectedRef.current = true;

      injectGA();
      injectTikTok();
      injectClarity();

      // Clean up listeners – no longer needed
      cleanup();
    }

    /* ---------- Signal listeners (real interactions only — no dwell timer) ---------- */
    const scrollHandler = () => activate();
    const moveHandler = () => activate();
    const touchHandler = () => activate();
    const clickHandler = () => activate();
    const keyHandler = () => activate();

    window.addEventListener("scroll", scrollHandler, { once: true, passive: true });
    window.addEventListener("mousemove", moveHandler, { once: true, passive: true });
    window.addEventListener("touchstart", touchHandler, { once: true, passive: true });
    window.addEventListener("click", clickHandler, { once: true, passive: true });
    window.addEventListener("keydown", keyHandler, { once: true });

    /* ---------- Cleanup ---------- */
    function cleanup() {
      window.removeEventListener("scroll", scrollHandler);
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("touchstart", touchHandler);
      window.removeEventListener("click", clickHandler);
      window.removeEventListener("keydown", keyHandler);
    }

    return cleanup;
  }, []);
}
